import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'uxd-design-system-components';
import Spinner from 'react-bootstrap/Spinner';
import ActiveValue from '~types/ActiveValue';
import CustomizePageProps from '~types/pages/CustomizePageProps';
import ValueCard from '../../../components/ValueCard/ValueCard';
import getText from '../../../assets/locale/locale';
import LocaleContext from '../../../contexts/LocaleContext';
import './CustomizePage.scss';
import StyledModal from '../../../components/StyledModal/StyledModal';
import addDataLayerTrigger from '../../../utils/addDataLayerTrigger/addDataLayerTrigger';
import WriteInModal from '../../../components/WriteInModal/WriteInModal';
import { sortValueList } from '../../../utils/valueStatementsUtil/valueStatementsSortUtil';
import ValueRow from '../../../components/ValueList/ValueRow/ValueRow';
import { usePreviousPath } from '../../../contexts/PreviousPathContext';
import { useDataLayerInfo } from '../../../contexts/DataLayerContext';
import { useLocation } from 'react-router-dom';

const CustomizePage = (props: CustomizePageProps) => {
  const { dataLayerInfo } = useDataLayerInfo();
  const {
    valueList,
    setInitialValuePair,
    customizePageBtnCallback,
    restart,
    isLoading,
  } = props;
  const locId = useContext(LocaleContext);
  const [isMobile, setIsMobile] = useState(false);
  const [startQuizLabel, setStartQuizLabel] = useState(
    getText(locId, 'disabledBtnDefault', 'customizePage')
  );
  const [quizDisabled, setQuizDisabled] = useState(true);
  const [localValuesList, setLocalValuesList] = useState<ActiveValue[]>([]);
  const [numValuesToRemove, setNumValuesToRemove] = useState(5);
  const [
    showWhyElevenPrioritiesInfoModal,
    setShowWhyElevenPrioritiesInfoModal,
  ] = useState(false);
  const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState(false);
  const [
    showCustomPriorityEditModal,
    setShowCustomPriorityEditModal,
  ] = useState(false);
  const [editModalCurrentCardValue, setEditModalCurrentCardValue] = useState({
    statementId: -1,
    statementText: '',
  });
  const writeInStatementId = 15;
  const maximumPhoneWidth = 768;

  const SPACE = ' ';
  const ENTER = 'Enter';

  const [infoModalContent, setInfoModalContent] = useState('');

  const handleMobileView = () =>
    setIsMobile(window.innerWidth < maximumPhoneWidth);

  const updatedFaInfo = {
    ...dataLayerInfo,
    page_type: 'customize-page',
  };

  const { setPreviousPath, previousPath } = usePreviousPath();
  const currentLocation = useLocation();
  const pageTitle = getText(locId, 'pageTitle', 'customizePage');
  const pageReferrer = previousPath ? `${window.location.origin}${previousPath}${currentLocation.search}` : undefined;
  const pageURL = `${window.location.origin}${currentLocation.pathname}${currentLocation.search}`;
  const virtualPageInfo = {
    'pagePath': currentLocation.pathname,
    'pageTitle': pageTitle,
    'page_referrer': pageReferrer,
    'external_referrer': window.document.referrer && window.document.referrer.trim() !== '' ? window.document.referrer : undefined,
    'page_url': pageURL
  };

  useEffect(() => {
    document.title = pageTitle;
    handleMobileView();
    window.removeEventListener('resize', handleMobileView);
    window.addEventListener('resize', handleMobileView);
  }, []);

  useEffect(() => {
    if (valueList.length > 0 && valueList[0]) {
      setPreviousPath(currentLocation.pathname);
      addDataLayerTrigger("dataLayer-initialized", updatedFaInfo);
      addDataLayerTrigger('VirtualPageview', virtualPageInfo);
      let tempValueList;
      if (!restart) {
        tempValueList = sortValueList(
          valueList.map((value: ActiveValue) => {
            return {
              statementId: value.statementId,
              statementRank: value.statementRank,
              statementText: value.statementText,
              learnMoreContent: value.learnMoreContent,
              learnMoreLinks: value.learnMoreLinks,
              isActive: true,
              indexId: value.indexId,
              additionalInfo: value.additionalInfo,
            };
          })
        );
      } else {
        tempValueList = sortValueList(valueList);
        setNumValuesToRemove(0);
      }
      setLocalValuesList(tempValueList);
    }
  }, [valueList]);

  useEffect(() => {
    if (numValuesToRemove > 0) {
      setStartQuizLabel(
        `${getText(
          locId,
          'disabledBtnRemove',
          'customizePage'
        )} ${numValuesToRemove} ${getText(
          locId,
          'disabledBtnMore',
          'customizePage'
        )}`
      );
      setQuizDisabled(true);
    } else if (numValuesToRemove < 0) {
      setStartQuizLabel(
        `${getText(locId, 'disabledBtnAdd', 'customizePage')} ${Math.abs(
          numValuesToRemove
        )} ${getText(locId, 'disabledBtnMore', 'customizePage')}`
      );
      setQuizDisabled(true);
    } else {
      setStartQuizLabel(getText(locId, 'startQuiz', 'customizePage'));
      setQuizDisabled(false);
    }
  }, [numValuesToRemove]);

  useEffect(() => {
    if (localValuesList.length > 0 && localValuesList[0]) {
      const newLocalValuesList = localValuesList.map(value => {
        if (value.statementId === editModalCurrentCardValue.statementId) {
          return {
            ...value,
            statementText: editModalCurrentCardValue.statementText,
          };
        }
        return value;
      });
      setLocalValuesList(newLocalValuesList);
    }
  }, [editModalCurrentCardValue]);

  function handleCardClick(valueId: number) {
    setLocalValuesList(
      localValuesList.map(value => {
        if (value.statementId === valueId) {
          // If currently active, reduce numsValueToRemove since it will be switched to disabled
          setNumValuesToRemove(
            value.isActive ? numValuesToRemove - 1 : numValuesToRemove + 1
          );
          return {
            statementId: value.statementId,
            statementRank: value.statementRank,
            statementText: value.statementText,
            learnMoreContent: value.learnMoreContent,
            learnMoreLinks: value.learnMoreLinks,
            isActive: !value.isActive,
            indexId: value.indexId,
            additionalInfo: value.additionalInfo,
          };
        }
        return value;
      })
    );
  }

  function finalizeValueSelections() {
    let indexNum = 0;
    const finalValuesList = Array.from(localValuesList);

    finalValuesList.forEach((value, index) => {
      if (value.isActive) {
        finalValuesList[index].indexId = indexNum;
        indexNum += 1;
      } else {
        finalValuesList[index].indexId = -1;
      }
    });

    setInitialValuePair(finalValuesList);
    return finalValuesList;
  }

  const mapValuesToCustomizeCards = () => {
    return localValuesList.map(value => {
      return value ? (
        <div
          className="CustomizePage_card"
          key={value.statementId}
          role="button"
          onClick={() => handleCardClick(value.statementId)}
          onKeyDown={(e) => {
            if (e.key === ENTER || e.key === SPACE) {
              e.preventDefault();
              handleCardClick(value.statementId);
            }
          }}
          tabIndex={0}
          aria-pressed={value.isActive}
          data-testid={`valueCardContainer-${value.statementId}`}
        >
          <ValueCard
            valueStatement={value.statementText}
            valueStatementId={value.statementId}
            disabled={!value.isActive}
            setShowAdditionalInfoModal={setShowAdditionalInfoModal}
            setShowCustomPriorityEditModal={setShowCustomPriorityEditModal}
            isEditable={value.statementId === writeInStatementId}
            setEditModalCurrentCardValue={setEditModalCurrentCardValue}
            infoModalContent={value.additionalInfo}
            setInfoModalContent={setInfoModalContent}
          />
        </div>
      ) : null;
    });
  };

  const mapValuesToCustomizeRows = () => {
    return localValuesList.map(value => {
      return value ? (
        <div
          className="CustomizePage_row"
          key={value.statementId}
          role="button"
          onClick={e => {
            e.preventDefault();
            handleCardClick(value.statementId);
          }}
          onKeyDown={e => {
            e.stopPropagation();
            if (e.key === ENTER || e.key === SPACE) {
              e.preventDefault();
              handleCardClick(value.statementId);
            }
          }}
          tabIndex={0}
        >
          <ValueRow
            valueStatement={value.statementText}
            valueStatementId={value.statementId}
            disabled={!value.isActive}
            setShowAdditionalInfoModal={setShowAdditionalInfoModal}
            setShowCustomPriorityEditModal={setShowCustomPriorityEditModal}
            isEditable={value.statementId === writeInStatementId}
            setEditModalCurrentCardValue={setEditModalCurrentCardValue}
            infoModalContent={value.additionalInfo}
            setInfoModalContent={setInfoModalContent}
          />
        </div>
      ) : null;
    });
  };

  const doesValuesListContainInvalidWriteInValue = () => {
    const writeInPriority = localValuesList.find(
      value => value.statementId === writeInStatementId
    );
    return (
      writeInPriority &&
      writeInPriority.isActive &&
      (!writeInPriority.statementText ||
        writeInPriority.statementText.toLowerCase() ===
        getText(locId, 'writeInPlaceHolderText', 'customizePage'))
    );
  };

  const handleStartBtnClick = () => {
    if (!doesValuesListContainInvalidWriteInValue()) {
      addDataLayerTrigger('get-started-customize-page', {});
      window.removeEventListener('resize', handleMobileView);
      customizePageBtnCallback(finalizeValueSelections());
    } else {
      const writeInPriority = localValuesList.find(
        value => value.statementId === writeInStatementId
      );
      setEditModalCurrentCardValue({
        statementId: writeInPriority.statementId,
        statementText: writeInPriority.statementText,
      });
      setShowCustomPriorityEditModal(true);
    }
  };

  return (
    <div className="CustomizePage" data-testid="customize-page">
      <div className="CustomizePage_content">
        <div className="CustomizePage_intro">
          <h1 id="heading-no-focus" className="heading--semi-bold">
            {getText(locId, 'header', 'customizePage')}
          </h1>
          <div className="CustomizePage_headerDesc">
            <p className="CustomizePage_p">
              {getText(locId, 'description', 'customizePage')}
            </p>
            <Button
              label={getText(locId, 'iconButton', 'customizePage')}
              icon="help-question-mark"
              styleVariant="tertiary"
              onClick={() => {
                setShowWhyElevenPrioritiesInfoModal(true);
              }}
              attributes={{
                value: '',
                id: 'modal-icon-button',
              }}
            />
          </div>
        </div>
        <div className="CustomizePage_btnContainer">
          <span aria-live="polite"
            aria-relevant="text"
            aria-atomic="true" className="CustomizePage_visually-hidden">{startQuizLabel}</span>
          <Button
            label={startQuizLabel}
            disabled={quizDisabled}
            onClick={() => handleStartBtnClick()}
            attributes={{
              value: '',
              id: 'get-started-button',
            }}
          />
        </div>
        {isMobile ? (
          <>
            <div className="CustomizePage_rowsListContainer">
              {isLoading ? (
                <div id="spinner" data-testid="customize-spinner">
                  <Spinner animation="border" />
                </div>
              ) : (
                mapValuesToCustomizeRows()
              )}
            </div>
            <div className="CustomizePage_btnContainer">
              <Button
                label={startQuizLabel}
                disabled={quizDisabled}
                onClick={() => handleStartBtnClick()}
                attributes={{
                  value: '',
                  id: 'get-started-button',
                }}
              />
            </div>
          </>
        ) : (
          <div className="CustomizePage_cardsContainer">
            {isLoading ? (
              <div id="spinner" data-testid="customize-spinner">
                <Spinner animation="border" />
              </div>
            ) : (
              mapValuesToCustomizeCards()
            )}
          </div>
        )}
        <StyledModal
          displayModal={showWhyElevenPrioritiesInfoModal}
          closeModalHandler={() => setShowWhyElevenPrioritiesInfoModal(false)}
          headerText={getText(locId, 'modalTitle', 'customizePage')}
          paragraphText1={getText(locId, 'modalParagraph1', 'customizePage')}
          paragraphText2={getText(locId, 'modalParagraph2', 'customizePage')}
        />
        <StyledModal
          displayModal={showAdditionalInfoModal}
          closeModalHandler={() => setShowAdditionalInfoModal(false)}
          headerText={infoModalContent}
        />
        <WriteInModal
          currentCardValue={editModalCurrentCardValue}
          setCurrentCardValue={setEditModalCurrentCardValue}
          showModal={showCustomPriorityEditModal}
          closeModalHandler={() => setShowCustomPriorityEditModal(false)}
        />
      </div>
      <div className="footer">
        <div className="disclaimer">
          <p className="microcopy--2">
            {getText(locId, 'welcomePageDisclosure', 'welcomePage')}
          </p>
        </div>
      </div>
    </div>
  );
};

CustomizePage.propTypes = {
  valueList: PropTypes.arrayOf(
    PropTypes.shape({
      statementId: PropTypes.number,
      statementText: PropTypes.string,
      statementRank: PropTypes.number,
      isActive: PropTypes.bool,
      indexId: PropTypes.number,
    })
  ).isRequired,
  setInitialValuePair: PropTypes.func.isRequired,
  customizePageBtnCallback: PropTypes.func.isRequired,
  restart: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default CustomizePage;
